<template>
  <div class="space-y-6">
    <HermesCard title="Run the tests" :meta="`${caseLabels.length} cases · judge ${bench.executor.model}`">
      <div class="grid grid-cols-12 gap-4">
        <label class="col-span-3">
          <span class="block text-gray-500 mb-2">Model <span class="text-gray-400">· blank for the deployed default</span></span>
          <input v-model="form.model" :class="INPUT" list="bench-run-models" placeholder="gemini-3.8-flash">
          <datalist id="bench-run-models"><option v-for="m in models" :key="m" :value="m" /></datalist>
        </label>
        <label class="col-span-2">
          <span class="block text-gray-500 mb-2">Reasoning</span>
          <select v-model="form.reasoning" :class="INPUT"><option value="">default</option><option v-for="r in ['minimal', 'low', 'medium', 'high']" :key="r" :value="r">{{ r }}</option></select>
        </label>
        <label class="col-span-2">
          <span class="block text-gray-500 mb-2">Hats</span>
          <select v-model="form.hats" :class="INPUT"><option value="">default</option><option value="on">on</option><option value="off">off</option></select>
        </label>
        <label class="col-span-5">
          <span class="block text-gray-500 mb-2">Cases <span class="text-gray-400">· all unless you pick some</span></span>
          <select v-model="form.only" multiple :class="[INPUT, 'h-24']">
            <option v-for="l in caseLabels" :key="l" :value="l">{{ l }}</option>
          </select>
        </label>
      </div>
      <div class="flex items-center gap-3 mt-4">
        <button type="button" :class="PRIMARY_BUTTON" :disabled="starting" @click="start">{{ starting ? 'Starting…' : 'Run' }}</button>
        <span v-if="estimate" class="text-gray-600">{{ estimate }}</span>
        <span v-if="startError" class="text-red-600">{{ startError }}</span>
      </div>
    </HermesCard>

    <div class="flex items-center gap-3">
      <span class="text-gray-600">Tick two runs to compare them.</span>
      <button v-if="picked.length" type="button" :class="[TEXT_BUTTON, 'ml-auto']" @click="picked = []">Clear</button>
    </div>

    <TestCompare v-if="picked.length === 2" :bench="bench" :a="picked[0]" :b="picked[1]" />

    <div class="rounded-md border border-gray-200 overflow-hidden">
      <table class="w-full table-fixed">
        <colgroup><col style="width:4%"><col style="width:7%"><col style="width:14%"><col style="width:22%"><col style="width:10%"><col style="width:15%"><col style="width:8%"><col style="width:8%"><col style="width:6%"><col style="width:6%"></colgroup>
        <thead class="bg-gray-50"><tr>
          <th :class="TH" />
          <th :class="TH">Run</th><th :class="TH">Started</th><th :class="TH">Settings</th><th :class="TH">Status</th><th :class="TH">Passed</th>
          <th :class="[TH, 'text-right']">Judge</th><th :class="[TH, 'text-right']">Cost</th><th :class="[TH, 'text-right']">Time</th><th :class="[TH, 'text-right']" />
        </tr></thead>
        <tbody class="divide-y divide-gray-200">
          <tr v-if="loading && !runs.length"><td colspan="10" class="px-3 py-8 text-center text-gray-500">Loading…</td></tr>
          <tr v-else-if="!runs.length"><td colspan="10" class="px-3 py-8 text-center text-gray-500">No runs yet.</td></tr>
          <template v-for="r in runs" :key="r.id">
            <tr class="cursor-pointer hover:bg-neutral-50" :class="open === r.id ? 'bg-neutral-50' : ''" @click="toggle(r.id)">
              <td :class="TD" @click.stop><input type="checkbox" :checked="picked.includes(r.id)" :disabled="!picked.includes(r.id) && picked.length >= 2" @change="pick(r.id)"></td>
              <td :class="TD">#{{ r.id }}</td>
              <td :class="TD"><span :title="absolute(r.created_at)">{{ relative(r.created_at) }}</span><span class="block text-gray-500">{{ r.requested_by_name }}</span></td>
              <td :class="TD">{{ paramsLine(r) }}</td>
              <td :class="TD"><HermesBadge :value="r.status" /></td>
              <td :class="TD">{{ passLine(r) || '—' }}</td>
              <td :class="[TD, 'text-right tabular-nums']">{{ judge(r) }}</td>
              <td :class="[TD, 'text-right tabular-nums']">{{ money((r.usage || {}).cost_usd) }}</td>
              <td :class="[TD, 'text-right tabular-nums']">{{ duration(r.started_at, r.finished_at) }}</td>
              <td :class="[TD, 'text-right']" @click.stop>
                <button v-if="['pending', 'running'].includes(r.status)" type="button" :class="TEXT_BUTTON" @click="cancel(r)">Cancel</button>
              </td>
            </tr>
            <tr v-if="open === r.id">
              <td colspan="10" class="px-4 py-4 bg-neutral-50/60">
                <TestRunResults :bench="bench" :run-id="r.id" />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <p v-if="error" class="text-red-600">{{ error }}</p>
  </div>
</template>

<script>
import HermesBadge from '../HermesBadge.vue';
import HermesCard from '../HermesCard.vue';
import { API, INPUT, PRIMARY_BUTTON, TD, TEXT_BUTTON, TH, absolute, duration, errorOf, money, relative } from '../hermes';
import { casesOf, onBench, paramsLine, passLine } from './bench';
import TestRunResults from './TestRunResults.vue';
import TestCompare from './TestCompare.vue';

export default {
  name: 'TestRuns',
  components: { HermesBadge, HermesCard, TestRunResults, TestCompare },
  props: { bench: { type: Object, required: true } },
  data() {
    return {
      form: { model: '', reasoning: '', hats: '', only: [] }, models: ['claude-sonnet-5-5', 'gemini-3.8-flash', 'claude-opus-5-5'],
      runs: [], loading: true, error: '', starting: false, startError: '', timer: null, open: null, picked: [],
      INPUT, PRIMARY_BUTTON, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    caseLabels() {
      return casesOf(this.bench.template).map((c) => c.label);
    },
    estimate() {
      const done = this.runs.find((r) => r.status === 'completed' && (r.progress || {}).done && (r.usage || {}).cost_usd);
      if (!done) return '';
      const per = done.usage.cost_usd / done.progress.done;
      const n = this.form.only.length || this.caseLabels.length;
      return `About ${money(per * n)} for ${n} cases, from run #${done.id}.`;
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    absolute, duration, money, relative, paramsLine, passLine,
    judge(r) {
      const j = (r.metadata || {}).judge_avg;
      return j === null || j === undefined ? '—' : `${j} / 5`;
    },
    toggle(id) {
      this.open = this.open === id ? null : id;
    },
    pick(id) {
      this.picked = this.picked.includes(id) ? this.picked.filter((x) => x !== id) : [...this.picked, id].slice(-2);
    },
    async load() {
      clearTimeout(this.timer);
      this.error = '';
      try {
        const { data } = await this.$axios.get(`${API}/runs/`, onBench(this.bench, { params: { template: this.bench.template.id, limit: 50 } }));
        this.runs = data.results;
        if (this.runs.some((r) => ['pending', 'running'].includes(r.status))) this.timer = setTimeout(this.load, 5000);
      } catch (e) {
        this.error = errorOf(e, 'Runs could not be loaded.');
      } finally {
        this.loading = false;
      }
    },
    async start() {
      this.starting = true;
      this.startError = '';
      const params = {};
      if (this.form.model.trim()) params.model = this.form.model.trim();
      if (this.form.reasoning) params.reasoning = this.form.reasoning;
      if (this.form.hats) params.hats = this.form.hats;
      if (this.form.only.length) params.only = this.form.only.join(',');
      try {
        const { data } = await this.$axios.post(`${API}/runs/start/`, { executor: this.bench.executor.key, template: this.bench.template.id, params }, onBench(this.bench));
        this.open = data.id;
        await this.load();
      } catch (e) {
        this.startError = errorOf(e, 'The run was not started.');
      } finally {
        this.starting = false;
      }
    },
    async cancel(r) {
      try {
        await this.$axios.post(`${API}/runs/${r.id}/cancel/`, null, onBench(this.bench));
        this.load();
      } catch (e) {
        this.error = errorOf(e, 'The run could not be cancelled.');
      }
    },
  },
};
</script>
